import { z } from 'zod';

/**
 * A tenant's web settings (docs/claude/forms/01-design.md §8): how long web form
 * submissions and the data around them are kept, and the versions of the Privacy Policy
 * and Terms of Service a `terms` confirmation refers to. The tenant's values live in the
 * `web.settings` instance setting (Settings → Ecommerce → Web Settings); a brand may
 * override any of them in `portal_site.settings.webSettings` (the Portal Site editor).
 *
 * One schema serves the server (which is the authority) and the screens.
 */

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function days(label: string, min: number): z.ZodNumber {
	return z
		.number()
		.int(`${label} must be a whole number of days`)
		.min(min, `${label} must be at least ${min} ${min === 1 ? 'day' : 'days'}`)
		.max(3650, `${label} can be at most 3650 days (10 years)`);
}

// The retention rules without defaults, so a brand override can leave any of them out.
const RetentionFields = {
	/** Every submission is deleted after this. */
	submissionsDays: days('Submissions', 30),
	/** Spam and duplicates are deleted sooner. */
	spamDays: days('Spam and duplicates', 1),
	/** The submitter's IP address and browser information are cleared after this. */
	ipAddressDays: days('IP address and browser information', 1),
	/** Off: IP address and browser information are never stored (and cleared nightly). */
	storeIpAddress: z.boolean(),
};

// A policy version is the date its text last changed, or null when none is recorded.
const PolicyVersionSchema = z
	.string()
	.regex(DATE_PATTERN, 'A policy version is a date (YYYY-MM-DD)')
	.nullable();

export const WebRetentionSchema = z.object({
	submissionsDays: RetentionFields.submissionsDays.default(730),
	spamDays: RetentionFields.spamDays.default(90),
	ipAddressDays: RetentionFields.ipAddressDays.default(90),
	storeIpAddress: RetentionFields.storeIpAddress.default(true),
});

export const WebPolicyVersionsSchema = z.object({
	privacy: PolicyVersionSchema.default(null),
	terms: PolicyVersionSchema.default(null),
});

export const WebSettingsSchema = z.object({
	retention: WebRetentionSchema,
	policyVersions: WebPolicyVersionsSchema,
});

/** A brand's override: only the values it sets differ from the tenant's. */
export const WebSettingsOverrideSchema = z.object({
	// Built from the default-free rules: `.partial()` on the defaulted schema would fill in
	// every value left out, and the brand would silently override all of them.
	retention: z.object(RetentionFields).partial().optional(),
	policyVersions: z
		.object({ privacy: PolicyVersionSchema, terms: PolicyVersionSchema })
		.partial()
		.optional(),
});

export type WebRetentionModel = z.infer<typeof WebRetentionSchema>;
export type WebPolicyVersionsModel = z.infer<typeof WebPolicyVersionsSchema>;
export type WebSettingsModel = z.infer<typeof WebSettingsSchema>;
export type WebSettingsOverrideModel = z.infer<
	typeof WebSettingsOverrideSchema
>;

export const DEFAULT_WEB_SETTINGS: Readonly<WebSettingsModel> = {
	retention: {
		submissionsDays: 730,
		spamDays: 90,
		ipAddressDays: 90,
		storeIpAddress: true,
	},
	policyVersions: { privacy: null, terms: null },
};

/**
 * The values that apply to one brand: the tenant's, with the brand's override on top. A
 * value missing on either side falls back to the default, so a half-written setting can
 * never leave a window undefined.
 */
export function resolveWebSettings(
	tenant: Partial<WebSettingsModel> | null | undefined,
	override?: WebSettingsOverrideModel | null,
): WebSettingsModel {
	return {
		retention: {
			...DEFAULT_WEB_SETTINGS.retention,
			...definedOnly(tenant?.retention),
			...definedOnly(override?.retention),
		},
		policyVersions: {
			...DEFAULT_WEB_SETTINGS.policyVersions,
			...definedOnly(tenant?.policyVersions),
			...definedOnly(override?.policyVersions),
		},
	};
}

/**
 * The policy versions a `terms` confirmation records, `{ privacy: '2026-09-24', … }`,
 * leaving out a policy with no version recorded. Null when none is recorded.
 */
export function recordedPolicyVersions(
	versions: WebPolicyVersionsModel,
): Record<string, string> | null {
	const recorded: Record<string, string> = {};
	if (versions.privacy != null) {
		recorded['privacy'] = versions.privacy;
	}
	if (versions.terms != null) {
		recorded['terms'] = versions.terms;
	}
	return Object.keys(recorded).length > 0 ? recorded : null;
}

function definedOnly<T extends object>(
	values: Partial<T> | null | undefined,
): Partial<T> {
	if (values == null) {
		return {};
	}
	return Object.fromEntries(
		Object.entries(values).filter(([, value]) => value !== undefined),
	) as Partial<T>;
}

/**
 * Whether two brand overrides say the same thing. Compared field by field in a fixed
 * order, because a stored JSON object comes back from MySQL with its keys reordered.
 */
export function sameWebSettingsOverride(
	a: WebSettingsOverrideModel | null | undefined,
	b: WebSettingsOverrideModel | null | undefined,
): boolean {
	return (
		JSON.stringify(overrideInFixedOrder(a)) ===
		JSON.stringify(overrideInFixedOrder(b))
	);
}

function overrideInFixedOrder(
	override: WebSettingsOverrideModel | null | undefined,
): Array<unknown> {
	const retention = override?.retention ?? {};
	const policyVersions = override?.policyVersions ?? {};
	// Unset (use the tenant's value) is not the same as an explicit null policy version.
	return [
		retention.submissionsDays,
		retention.spamDays,
		retention.ipAddressDays,
		retention.storeIpAddress,
		policyVersions.privacy,
		policyVersions.terms,
	].map((value) => (value === undefined ? { unset: true } : value));
}
